<script lang="ts">
import { defineComponent, nextTick, onMounted, onUnmounted, PropType, Ref, ref, watch } from "vue";
import * as d3 from "d3";
import { SpectrogramAnnotation, SpectrogramSequenceAnnotation } from "../../api/api";
import {
  annotationSpreadAcrossPulsesWarning,
  geojsonToSpectro,
  SpectroInfo,
  textColorFromBackground,
} from "./geoJSUtils";
import EditAnnotationLayer from "./layers/editAnnotationLayer";
import RectangleLayer from "./layers/rectangleLayer";
import CompressedOverlayLayer from "./layers/compressedOverlayLayer";
import SequenceLayer from "./layers/sequenceLayer";
import LegendLayer from "./layers/legendLayer";
import TimeLayer from "./layers/timeLayer";
import FreqLayer from "./layers/freqLayer";
import SpeciesLayer from "./layers/speciesLayer";
import SpeciesSequenceLayer from "./layers/speciesSequenceLayer";
import MeasureToolLayer from "./layers/measureToolLayer";
import BoundingBoxLayer from "./layers/boundingBoxLayer";
import AxesLayer from "./layers/axesLayer";
import ContourLayer from "./layers/contourLayer";
import PulseMetadataLayer from "./layers/pulseMetadataLayer";
import { cloneDeep } from "lodash";
import useState from "@use/useState";
import usePulseMetadata from "@use/usePulseMetadata";

export default defineComponent({
  name: "LayerManager",
  props: {
    geoViewerRef: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      type: Object as PropType<any>,
      required: true,
    },
    spectroInfo: {
      type: Object as PropType<SpectroInfo | undefined>,
      default: () => undefined,
    },
    thumbnail: {
      type: Boolean,
      default: false,
    },
    scaledWidth: {
      type: Number,
      default: -1,
    },
    scaledHeight: {
      type: Number,
      default: -1,
    },
    recordingId: {
      type: String as PropType<string | null>,
      required: true,
    },
  },
  emits: ["selected", "update:annotation", "create:annotation", "set-cursor"],
  setup(props, { emit }) {
    const {
      creationType,
      annotationState,
      setAnnotationState,
      layerVisibility,
      selectedUsers,
      colorScale,
      currentUser,
      annotations,
      sequenceAnnotations,
      otherUserAnnotations,
      selectedId,
      selectedType,
      setSelectedId,
      viewCompressedOverlay,
      configuration,
      colorScheme,
      backgroundColor,
      measuring,
      frequencyRulerY,
      drawingBoundingBox,
      boundingBoxError,
      fixedAxes,
      contoursEnabled,
      contourOpacity,
      loadContours,
      computedPulseContours,
      transparencyThreshold,
    } = useState();
    const {
      viewPulseMetadataLayer,
      pulseMetadataList,
      loadPulseMetadata,
      clearPulseMetadata,
      pulseMetadataLineColor,
      pulseMetadataLineSize,
      pulseMetadataHeelColor,
      pulseMetadataCharFreqColor,
      pulseMetadataKneeColor,
      pulseMetadataPointSize,
      pulseMetadataShowLabels,
      pulseMetadataDurationFreqLineColor,
    } = usePulseMetadata();
    const selectedAnnotationId: Ref<null | number> = ref(null);
    const hoveredAnnotationId: Ref<null | number> = ref(null);
    const localAnnotations: Ref<SpectrogramAnnotation[]> = ref(cloneDeep(annotations.value));
    const localSequenceAnnotations: Ref<SpectrogramSequenceAnnotation[]> = ref(cloneDeep(sequenceAnnotations.value));
    const editing = ref(false);
    const editingAnnotation: Ref<null | SpectrogramAnnotation | SpectrogramSequenceAnnotation> = ref(null);
    let rectAnnotationLayer: RectangleLayer;
    let compressedOverlayLayer: CompressedOverlayLayer;
    let sequenceAnnotationLayer: SequenceLayer;
    let editAnnotationLayer: EditAnnotationLayer;
    let legendLayer: LegendLayer;
    let axesLayer: AxesLayer;
    let timeLayer: TimeLayer;
    let freqLayer: FreqLayer;
    let speciesLayer: SpeciesLayer;
    let speciesSequenceLayer: SpeciesSequenceLayer;
    let measureToolLayer: MeasureToolLayer;
    let boundingBoxLayer: BoundingBoxLayer;
    let contourLayer: ContourLayer;
    let pulseMetadataLayer: PulseMetadataLayer;
    const displayError = ref(false);
    const errorMsg = ref("");

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
    const sequenceEvent = (type: string, data: any) => {
      if (type === "annotation-clicked") {
        // click sequence annotation
        if (selectedAnnotationId.value !== null) {
          const foundIndex = sequenceAnnotations.value.findIndex(
            (item) => item.id === selectedAnnotationId.value
          );
          if (foundIndex !== -1) {
            editingAnnotation.value = sequenceAnnotations.value[foundIndex];
            const copy: SpectrogramSequenceAnnotation[] = cloneDeep(localSequenceAnnotations.value);
            copy[foundIndex].editing = true;
            localSequenceAnnotations.value = copy;
          }
        }
        selectedAnnotationId.value = data.id;
        editing.value = data.edit;
        editingAnnotation.value = null;
        selectedId.value = selectedAnnotationId.value;
        if (data.id !== null) {
          setSelectedId(selectedAnnotationId.value, 'sequence');
        }
        setSelectedId(selectedAnnotationId.value);
        triggerUpdate();
      }
      if (type === "annotation-right-clicked") {
        selectedAnnotationId.value = data.id;
        editing.value = data.edit;
        const foundIndex = localSequenceAnnotations.value.findIndex(
          (item) => item.id === selectedAnnotationId.value
        );
        if (editing.value && foundIndex !== -1) {
          editingAnnotation.value = localSequenceAnnotations.value[foundIndex];
          const copy: SpectrogramSequenceAnnotation[] = cloneDeep(localSequenceAnnotations.value);
          copy[foundIndex].editing = true;
          localSequenceAnnotations.value = copy;
        } else if (!editing.value && foundIndex !== -1) {
          editingAnnotation.value = null;
          localSequenceAnnotations.value[foundIndex].editing = undefined;
        }
        if (data.id !== null) {
          setSelectedId(selectedAnnotationId.value, 'sequence');
        }
        setSelectedId(selectedAnnotationId.value);
        triggerUpdate();
      }

    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
    const event = (type: string, data: any) => {
      // Will handle clicking, selecting and editing here
      if (type === "update:mode") {
        setAnnotationState(data.mode);
      }
      if (type === "update:cursor") {
        emit("set-cursor", data.cursor);
      }
      if (type === "annotation-cleared") {
        editing.value = false;
        selectedAnnotationId.value = null;
        editingAnnotation.value = null;
        editAnnotationLayer.disable();
        triggerUpdate();
        const copy: SpectrogramAnnotation[] = cloneDeep(localAnnotations.value);
        copy.forEach((item) => (item.editing = undefined));
        localAnnotations.value = copy;
        if (data.id !== null) {
          setSelectedId(selectedAnnotationId.value, 'pulse');
        }
        setSelectedId(selectedAnnotationId.value);
      }
      if (type === "annotation-clicked") {
        if (selectedAnnotationId.value !== null) {
          const foundIndex = localAnnotations.value.findIndex(
            (item) => item.id === selectedAnnotationId.value
          );
          if (foundIndex !== -1) {
            editingAnnotation.value = localAnnotations.value[foundIndex];
            const copy: SpectrogramAnnotation[] = cloneDeep(localAnnotations.value);
            copy[foundIndex].editing = true;
            localAnnotations.value = copy;
          }
        }
        selectedAnnotationId.value = data.id;
        editing.value = data.edit;
        editingAnnotation.value = null;
        selectedId.value = selectedAnnotationId.value;
        //emit("selected", selectedAnnotationId.value);
        if (data.id !== null) {
          setSelectedId(selectedAnnotationId.value, 'pulse');
        }
        setSelectedId(selectedAnnotationId.value);
        triggerUpdate();
      }
      if (type === "annotation-hover") {
        hoveredAnnotationId.value = data.id;
      }
      if (type === "annotation-right-clicked") {
        selectedAnnotationId.value = data.id;
        editing.value = data.edit;
        const foundIndex = localAnnotations.value.findIndex(
          (item) => item.id === selectedAnnotationId.value
        );
        if (editing.value && foundIndex !== -1) {
          editingAnnotation.value = localAnnotations.value[foundIndex];
          const copy: SpectrogramAnnotation[] = cloneDeep(localAnnotations.value);
          copy[foundIndex].editing = true;
          localAnnotations.value = copy;
        } else if (!editing.value && foundIndex !== -1) {
          editingAnnotation.value = null;
          localAnnotations.value[foundIndex].editing = undefined;
        }
        setSelectedId(selectedAnnotationId.value, 'pulse');
        triggerUpdate();
      }
      if (type === "update:geojson") {
        const status = data["status"];
        const creating = data["creating"];
        const geoJSON = data["geoJSON"];
        if (geoJSON && selectedAnnotationId.value !== null && status === "editing" && !creating) {
          if (annotationState.value !== "creating") {
            const index = selectedType.value === 'pulse' ? localAnnotations.value.findIndex(
              (item) => item.id === selectedAnnotationId.value
            ) : localSequenceAnnotations.value.findIndex((item) => item.id === selectedAnnotationId.value);
            if (index !== -1 && props.spectroInfo && selectedType.value === 'pulse') {
              // update bounds for the localAnnotation
              const conversionResult = geojsonToSpectro(geoJSON, props.spectroInfo, props.scaledWidth, props.scaledHeight);
              if (conversionResult.warning) {
                displayError.value = true;
                errorMsg.value = conversionResult.warning;
                return;
              }
              const { low_freq, high_freq, start_time, end_time } = conversionResult;
              localAnnotations.value[index] = {
                ...localAnnotations.value[index],
                low_freq,
                high_freq,
                start_time,
                end_time,
              };
              editingAnnotation.value = localAnnotations.value[index];
            }
            if (index !== -1 && props.spectroInfo && selectedType.value === 'sequence') {
              // update bounds for the localAnnotation
              const conversionResult = geojsonToSpectro(geoJSON, props.spectroInfo, props.scaledWidth, props.scaledHeight);
              if (conversionResult.warning && conversionResult.warning !== annotationSpreadAcrossPulsesWarning) {
                displayError.value = true;
                errorMsg.value = conversionResult.warning;
                return;
              }
              const { start_time, end_time } = conversionResult;
              localSequenceAnnotations.value[index] = {
                ...localSequenceAnnotations.value[index],
                start_time,
                end_time,
              };
              editingAnnotation.value = localSequenceAnnotations.value[index];
            }

            triggerUpdate();
            emit("update:annotation", editingAnnotation.value);
          }
        } else if (creating) {
          if (geoJSON && props.spectroInfo) {
            const conversionResult = geojsonToSpectro(geoJSON, props.spectroInfo, props.scaledWidth, props.scaledHeight);

            if (conversionResult.warning
              && !(creationType.value === 'sequence' && conversionResult.warning === annotationSpreadAcrossPulsesWarning)
            ) {
              displayError.value = true;
              errorMsg.value = conversionResult.warning;
              return;
            }
            const { low_freq, high_freq, start_time, end_time } = conversionResult;
            if (creationType.value === 'pulse') {
              const newAnnotation: SpectrogramAnnotation = {
                low_freq,
                high_freq,
                start_time,
                end_time,
                comments: "",
                species: [],
                id: 0,
              };
              emit("create:annotation", newAnnotation);
            } else if (creationType.value === 'sequence') {
              const newAnnotation: SpectrogramSequenceAnnotation = {
                start_time,
                end_time,
                species: [],
                type: '',
                comments: '',
                id: 0,
              };
              emit("create:annotation", newAnnotation);

            }
            editAnnotationLayer.disable();
            annotationState.value = "";
            editing.value = false;
          }
        }
      }
      if (type === "measure:dragged") {
        const { yValue } = data;
        frequencyRulerY.value = yValue || 0;
      }
      if (type === "bbox:error") {
        const { error } = data;
        boundingBoxError.value = error || '';
      }
    };

    const getDataForLayers = () => {
      if (selectedUsers.value.length) {
        // We add more annotations to the system
        let additionalAnnotations: SpectrogramAnnotation[] = [];
        let additionalSequenceAnnotations: SpectrogramSequenceAnnotation[] = [];
        for (let i = 0; i < selectedUsers.value.length; i += 1) {
          const newAnnotations = otherUserAnnotations.value[selectedUsers.value[i]]['annotations'];
          additionalAnnotations = additionalAnnotations.concat(newAnnotations);
          const newSequenceAnnotations = otherUserAnnotations.value[selectedUsers.value[i]]['sequence'];
          additionalSequenceAnnotations = additionalSequenceAnnotations.concat(newSequenceAnnotations);

        }
        additionalAnnotations = additionalAnnotations.concat(localAnnotations.value);
        additionalSequenceAnnotations = additionalSequenceAnnotations.concat(localSequenceAnnotations.value);
        return { annotations: additionalAnnotations, sequenceAnnotations: additionalSequenceAnnotations, colorScale };
      } else {
        return { annotations: localAnnotations.value, sequenceAnnotations: localSequenceAnnotations.value };
      }
    };
    const triggerUpdate = () => {
      // Check for selected and editing annotations;
      const { annotations, sequenceAnnotations } = getDataForLayers();
      if (rectAnnotationLayer) {
        rectAnnotationLayer.formatData(
          annotations,
          selectedType.value === 'pulse' ? selectedAnnotationId.value : null,
          currentUser.value,
          colorScale.value,
        );
        rectAnnotationLayer.redraw();
      }
      if (viewCompressedOverlay.value && compressedOverlayLayer && !props.spectroInfo?.compressedWidth && props.spectroInfo?.start_times && props.spectroInfo.end_times) {
        compressedOverlayLayer.formatData(props.spectroInfo.start_times, props.spectroInfo.end_times);
        compressedOverlayLayer.redraw();
      } else {
        compressedOverlayLayer?.disable();
      }
      if (sequenceAnnotationLayer && layerVisibility.value.includes('sequence')) {
        sequenceAnnotationLayer.formatData(
          sequenceAnnotations,
          selectedType.value === 'sequence' ? selectedAnnotationId.value : null,
          currentUser.value,
          colorScale.value,
        );
        sequenceAnnotationLayer.redraw();
      }
      if (!props.thumbnail) {
        if (legendLayer) {
          if (layerVisibility.value.includes("grid")) {
            legendLayer.setGridEnabled(true);
            axesLayer.setGridEnabled(true);
          } else {
            legendLayer.setGridEnabled(false);
            axesLayer.setGridEnabled(false);
          }
          legendLayer.redraw();
        }
        if (layerVisibility.value.includes("time") || layerVisibility.value.includes('duration')) {
          if (layerVisibility.value.includes("time")) {
            timeLayer.displayDuration = false;
          } else {
            timeLayer.displayDuration = true;
          }
          timeLayer.formatData(annotations, sequenceAnnotations);
          timeLayer.redraw();
        } else {
          timeLayer.disable();
        }
        if (layerVisibility.value.includes("freq")) {
          freqLayer.formatData(annotations);
          freqLayer.redraw();
        } else {
          freqLayer.disable();
        }
        if (layerVisibility.value.includes("species")) {
          speciesLayer.formatData(annotations);
          speciesLayer.redraw();
          speciesSequenceLayer.formatData(sequenceAnnotations);
          speciesSequenceLayer.redraw();
        } else {
          speciesLayer.disable();
          speciesSequenceLayer.disable();
        }
      }
      if (editing.value && editingAnnotation.value) {
        setTimeout(() => {
          editAnnotationLayer.changeData(editingAnnotation.value, selectedType.value);
        }, 0);
      }
      // We need to disable annotations that aren't required for different views
      if (!configuration.value.display_pulse_annotations) {
        rectAnnotationLayer?.disable();
        speciesLayer?.disable();
        freqLayer?.disable();
      }
      if (!configuration.value.display_sequence_annotations) {
        sequenceAnnotationLayer?.disable();
        speciesSequenceLayer?.disable();
      }
    };
    watch(
      annotations,
      () => {
        localAnnotations.value = annotations.value;
        triggerUpdate();
      }
    );
    watch(sequenceAnnotations, () => {
      localSequenceAnnotations.value = sequenceAnnotations.value;
      triggerUpdate();
    });
    watch(selectedUsers, () => triggerUpdate());
    watch(
      selectedId,
      () => {
        selectedAnnotationId.value = selectedId.value;
        if (
          editAnnotationLayer &&
          editAnnotationLayer.getMode() === "editing" &&
          selectedId.value === null
        ) {
          nextTick(() => {
            if (
              editAnnotationLayer &&
              editAnnotationLayer.getMode() === "disabled" &&
              selectedId.value === null
            ) {
              annotationState.value === 'disabled';
              editAnnotationLayer.featureLayer.clear();
            }
          });
          editAnnotationLayer.disable();
          return;
        }
        triggerUpdate();
      }
    );
    watch(() => props.recordingId, () => {
      computedPulseContours.value = [];
      clearPulseMetadata();
    });
    watch(contoursEnabled, async () => {
      if (props.thumbnail) {
        return;
      }
      if (!props.recordingId || !props.spectroInfo) {
        console.error('Could not load contours. Could not determine recording ID');
        return;
      }
      if (computedPulseContours.value.length === 0) {
        await loadContours(new Number(props.recordingId) as number);
      }
      if (!contourLayer) {
        contourLayer = new ContourLayer(
          props.geoViewerRef,
          event,
          props.spectroInfo,
          computedPulseContours.value,
          colorScheme.value.scheme,
        );
      }
      let zIndex = 0;
      if (axesLayer) {
        zIndex = axesLayer.lineLayer.zIndex() - 1;
      }
      contourLayer.contourLayer.zIndex(zIndex);
      contourLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);
      contourLayer.setContourOpacity(contourOpacity.value);
      if (contoursEnabled.value) {
        contourLayer.drawContours();
      } else {
        contourLayer.removeContours();
      }
    });
    watch(contourOpacity, () => {
      if (contourLayer && contoursEnabled.value) {
        contourLayer.setContourOpacity(contourOpacity.value);
        contourLayer.updateContourStyle();
      }
    });
    watch(
      [
        viewPulseMetadataLayer,
        () => props.recordingId,
        () => props.spectroInfo?.compressedWidth,
      ],
      async () => {
        if (props.thumbnail) return;
        if (!props.recordingId || !props.spectroInfo?.compressedWidth) return;
        if (viewPulseMetadataLayer.value) {
          if (pulseMetadataList.value.length === 0) {
            await loadPulseMetadata(Number(props.recordingId));
          }
          if (!pulseMetadataLayer) {
            pulseMetadataLayer = new PulseMetadataLayer(
              props.geoViewerRef,
              event,
              props.spectroInfo,
              pulseMetadataList.value,
            );
          }
          pulseMetadataLayer.spectroInfo = props.spectroInfo;
          pulseMetadataLayer.setStyle({
            lineColor: pulseMetadataLineColor.value,
            lineWidth: pulseMetadataLineSize.value,
            durationFreqLineColor: pulseMetadataDurationFreqLineColor.value,
            heelColor: pulseMetadataHeelColor.value,
            charFreqColor: pulseMetadataCharFreqColor.value,
            kneeColor: pulseMetadataKneeColor.value,
            pointRadius: pulseMetadataPointSize.value,
            showLabels: pulseMetadataShowLabels.value,
          });
          pulseMetadataLayer.setPulseMetadataList(pulseMetadataList.value);
          pulseMetadataLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);
          pulseMetadataLayer.redraw();
        } else if (pulseMetadataLayer) {
          pulseMetadataLayer.disable();
        }
      },
      { immediate: true },
    );
    watch(pulseMetadataList, () => {
      if (pulseMetadataLayer && viewPulseMetadataLayer.value && props.spectroInfo) {
        pulseMetadataLayer.setPulseMetadataList(pulseMetadataList.value);
        pulseMetadataLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);
        pulseMetadataLayer.redraw();
      }
    });
    watch(
      [
        pulseMetadataLineColor,
        pulseMetadataLineSize,
        pulseMetadataHeelColor,
        pulseMetadataCharFreqColor,
        pulseMetadataKneeColor,
        pulseMetadataPointSize,
        pulseMetadataShowLabels,
        pulseMetadataDurationFreqLineColor,
      ],
      () => {
        if (pulseMetadataLayer && viewPulseMetadataLayer.value) {
          pulseMetadataLayer.setStyle({
            lineColor: pulseMetadataLineColor.value,
            lineWidth: pulseMetadataLineSize.value,
            durationFreqLineColor: pulseMetadataDurationFreqLineColor.value,
            heelColor: pulseMetadataHeelColor.value,
            charFreqColor: pulseMetadataCharFreqColor.value,
            kneeColor: pulseMetadataKneeColor.value,
            pointRadius: pulseMetadataPointSize.value,
            showLabels: pulseMetadataShowLabels.value,
          });
          pulseMetadataLayer.redraw();
        }
      },
    );
    onUnmounted(() => {
      if (editAnnotationLayer) {
        editAnnotationLayer.destroy();
      }
      if (rectAnnotationLayer) {
        rectAnnotationLayer.destroy();
      }
      if (compressedOverlayLayer) {
        compressedOverlayLayer.destroy();
      }
      if (sequenceAnnotationLayer) {
        sequenceAnnotationLayer.destroy();
      }
      if (timeLayer) {
        timeLayer.destroy();
      }
      if (freqLayer) {
        freqLayer.destroy();
      }
      if (speciesLayer) {
        speciesLayer.destroy();
      }
      if (speciesSequenceLayer) {
        speciesSequenceLayer.destroy();
      }
      if (pulseMetadataLayer) {
        pulseMetadataLayer.destroy();
      }
    });

    function setAxes() {
      if (!legendLayer || !axesLayer) {
        return;
      }
      if (fixedAxes.value) {
        legendLayer.disable();
        axesLayer.enable();
      } else {
        axesLayer.disable();
        legendLayer.enable();
      }
    }

    const initLayers = () => {
      if (props.spectroInfo) {
        if (!compressedOverlayLayer) {
          compressedOverlayLayer = new CompressedOverlayLayer(props.geoViewerRef, props.spectroInfo);
        }
        compressedOverlayLayer.spectroInfo = props.spectroInfo;
        compressedOverlayLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);
        if (!editAnnotationLayer) {
          editAnnotationLayer = new EditAnnotationLayer(props.geoViewerRef, event, props.spectroInfo);
        }
        editAnnotationLayer.spectroInfo = props.spectroInfo;
        editAnnotationLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);
        if (!rectAnnotationLayer) {
          rectAnnotationLayer = new RectangleLayer(props.geoViewerRef, event, props.spectroInfo);
        }
        rectAnnotationLayer.spectroInfo = props.spectroInfo;
        rectAnnotationLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);

        if (!sequenceAnnotationLayer) {
          sequenceAnnotationLayer = new SequenceLayer(props.geoViewerRef, sequenceEvent, props.spectroInfo);
        } {
          sequenceAnnotationLayer.spectroInfo = props.spectroInfo;
          sequenceAnnotationLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);
        }
        rectAnnotationLayer.formatData(localAnnotations.value, selectedAnnotationId.value, currentUser.value, colorScale.value);
        rectAnnotationLayer.redraw();
        if (viewCompressedOverlay.value && compressedOverlayLayer && props.spectroInfo.start_times && props.spectroInfo.end_times) {
          compressedOverlayLayer.formatData(props.spectroInfo.start_times, props.spectroInfo.end_times);
          compressedOverlayLayer.redraw();
        }
        sequenceAnnotationLayer.formatData(localSequenceAnnotations.value, selectedAnnotationId.value, currentUser.value, colorScale.value);
        sequenceAnnotationLayer.redraw();
        if (!props.thumbnail) {
          if (!legendLayer) {
            legendLayer = new LegendLayer(props.geoViewerRef, event, props.spectroInfo);
          }
          legendLayer.spectroInfo = props.spectroInfo;
          legendLayer.createLabels();
          legendLayer.calcGridLines();
          legendLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);
          legendLayer.onPan();
          if (!axesLayer) {
            axesLayer = new AxesLayer(props.geoViewerRef, event, props.spectroInfo);
          }
          axesLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);
          if (layerVisibility.value.includes('grid')) {
            legendLayer.setGridEnabled(true);
            axesLayer.setGridEnabled(true);
          }
          if (!timeLayer) {
            timeLayer = new TimeLayer(props.geoViewerRef, event, props.spectroInfo);
          }
          timeLayer.spectroInfo = props.spectroInfo;
          timeLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);
          if (!freqLayer) {
            freqLayer = new FreqLayer(props.geoViewerRef, event, props.spectroInfo);
          }
          freqLayer.spectroInfo = props.spectroInfo;
          freqLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);

          if (!speciesSequenceLayer) {
            speciesSequenceLayer = new SpeciesSequenceLayer(props.geoViewerRef, event, props.spectroInfo);
          }
          speciesSequenceLayer.spectroInfo = props.spectroInfo;
          speciesSequenceLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);
          if (!speciesLayer) {
            speciesLayer = new SpeciesLayer(props.geoViewerRef, event, props.spectroInfo);
          }
          speciesLayer.spectroInfo = props.spectroInfo;
          speciesLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);

          if (!measureToolLayer) {
            measureToolLayer = new MeasureToolLayer(
              props.geoViewerRef,
              event,
              props.spectroInfo,
              measuring.value,
              frequencyRulerY.value
            );
            measureToolLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);
          }
          measureToolLayer.redraw();
          watch(measuring, () => {
            if (measuring.value) {
              measureToolLayer.enableDrawing();
            } else {
              measureToolLayer.disableDrawing();
            }
          });

          if (!boundingBoxLayer) {
            boundingBoxLayer = new BoundingBoxLayer(props.geoViewerRef, event, props.spectroInfo, drawingBoundingBox.value);
            boundingBoxLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);
          }
          watch(drawingBoundingBox, () => {
            if (drawingBoundingBox.value) {
              boundingBoxLayer.enableDrawing();
            } else {
              boundingBoxLayer.disableDrawing();
            }
          });

          timeLayer.setDisplaying({ pulse: configuration.value.display_pulse_annotations, sequence: configuration.value.display_sequence_annotations });
          timeLayer.formatData(localAnnotations.value, sequenceAnnotations.value);
          freqLayer.formatData(localAnnotations.value);
          speciesLayer.formatData(localAnnotations.value);
          speciesSequenceLayer.formatData(localSequenceAnnotations.value);

          if (legendLayer) {
            legendLayer.redraw();
          }
          if (layerVisibility.value.includes('species')) {
            speciesLayer.redraw();
            speciesSequenceLayer.redraw();
          } else {
            speciesLayer.disable();
            speciesSequenceLayer.disable();
          }
          timeLayer.disable();
          freqLayer.disable();
        }
        if (props.spectroInfo.compressedWidth && viewCompressedOverlay.value) {
          viewCompressedOverlay.value = false;
        }
      }
      setAxes();
    };
    onMounted(() => {
      initLayers();
      updateColorFilter();
    });

    watch(() => props.spectroInfo, () => initLayers());
    watch(layerVisibility, () => {
      if (!props.thumbnail && legendLayer) {
        triggerUpdate();
      }
    });
    watch([() => props.scaledWidth, () => props.scaledHeight], () => {
      const { annotations, sequenceAnnotations } = getDataForLayers();
      if (legendLayer) {
        legendLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);
      }
      if (rectAnnotationLayer) {
        rectAnnotationLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);
        rectAnnotationLayer.formatData(
          annotations,
          selectedType.value === 'pulse' ? selectedAnnotationId.value : null,
          currentUser.value,
          colorScale.value,
        );
        rectAnnotationLayer.redraw();
      }
      if (compressedOverlayLayer && props.spectroInfo?.start_times && props.spectroInfo.end_times && viewCompressedOverlay.value) {
        if (!props.thumbnail) {
          compressedOverlayLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);
          compressedOverlayLayer.formatData(props.spectroInfo.start_times, props.spectroInfo.end_times);
          compressedOverlayLayer.redraw();
        } else {
          compressedOverlayLayer?.disable();
        }
      }
      if (contourLayer) {
        contourLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);
        if (contoursEnabled.value) {
          contourLayer.updateContourStyle();
        }
      }
      if (pulseMetadataLayer && viewPulseMetadataLayer.value) {
        pulseMetadataLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);
      }
      editAnnotationLayer?.setScaledDimensions(props.scaledWidth, props.scaledHeight);
      if (editing.value && editingAnnotation.value) {
        setTimeout(() => {
          editAnnotationLayer.changeData(editingAnnotation.value, selectedType.value);
        }, 0);
      }

      timeLayer?.setScaledDimensions(props.scaledWidth, props.scaledHeight);
      freqLayer?.setScaledDimensions(props.scaledWidth, props.scaledHeight);
      speciesLayer?.setScaledDimensions(props.scaledWidth, props.scaledHeight);
      speciesSequenceLayer?.setScaledDimensions(props.scaledWidth, props.scaledHeight);
      sequenceAnnotationLayer?.setScaledDimensions(props.scaledWidth, props.scaledHeight);
      if (timeLayer && (layerVisibility.value.includes("time") || layerVisibility.value.includes('duration'))) {
        if (layerVisibility.value.includes("time")) {
          timeLayer.displayDuration = false;
        } else {
          timeLayer.displayDuration = true;
        }
        timeLayer.formatData(annotations, sequenceAnnotations);
        timeLayer.redraw();
      }
      if (freqLayer && layerVisibility.value.includes("freq")) {
        freqLayer.formatData(annotations);
        freqLayer.redraw();
      } else {
        freqLayer?.disable();
      }
      if (speciesLayer && speciesSequenceLayer && layerVisibility.value.includes("species")) {
        speciesLayer.formatData(annotations);
        speciesLayer.redraw();
        speciesSequenceLayer.formatData(sequenceAnnotations);
        speciesSequenceLayer.redraw();
      } else {
        speciesLayer?.disable();
        speciesSequenceLayer?.disable();
      }
      if (sequenceAnnotationLayer && layerVisibility.value.includes('sequence')) {
        sequenceAnnotationLayer.formatData(
          sequenceAnnotations,
          selectedAnnotationId.value,
          currentUser.value,
          colorScale.value,
        );
        sequenceAnnotationLayer.redraw();
      }
      if (measureToolLayer) {
        measureToolLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);
        measureToolLayer.redraw();
      }
      // Triggers the Axis redraw when zoomed in and the axis is at the bottom/top
      legendLayer?.onPan();
      axesLayer?.setScaledDimensions(props.scaledWidth, props.scaledHeight);
    });
    watch(viewCompressedOverlay, () => {
      if (viewCompressedOverlay.value && compressedOverlayLayer && props.spectroInfo?.start_times && props.spectroInfo.end_times) {
        compressedOverlayLayer.setScaledDimensions(props.scaledWidth, props.scaledHeight);
        compressedOverlayLayer.formatData(props.spectroInfo.start_times, props.spectroInfo.end_times);
        compressedOverlayLayer.redraw();
      } else {
        compressedOverlayLayer.disable();
      }
    });
    watch(
      () => annotationState.value,
      () => {
        if (!props.thumbnail && annotationState.value === "creating") {
          editing.value = false;
          selectedAnnotationId.value = null;
          editingAnnotation.value = null;
          editAnnotationLayer.changeData(null, selectedType.value);
          triggerUpdate();
        }
      }
    );
    // Color scheme
    const rValues = ref('');
    const gValues = ref('');
    const bValues = ref('');

    function getTransparencyTableValues() {
      // transparencyThreshold is expected to be 0-100 (percentage)
      // Convert to normalized 0-1
      const t = (transparencyThreshold.value ?? 0) / 100;

      // quick edge cases
      if (t <= 0) {
        // threshold 0% → everything visible (alpha=1)
        return "1";
      }
      if (t >= 1) {
        // threshold 100% → everything transparent (alpha=0)
        return "0";
      }
      // number of discrete steps (higher = sharper but costlier)
      const numSteps = 100;
      // which index is the last 'transparent' bucket
      const thresholdStep = Math.floor(t * (numSteps - 1));

      const values: string[] = [];
      for (let i = 0; i < numSteps; i++) {
        // for i <= thresholdStep set transparent (0), else opaque (1)
        values.push(i <= thresholdStep ? "0" : "1");
      }
      return values.join(" ");
    }

    function updateColorFilter() {
      if (!backgroundColor.value.includes(',')) {
        // convert rgb(0 0 0) to rgb(0, 0, 0)
        backgroundColor.value = backgroundColor.value.replace(/rgb\((\d+)\s+(\d+)\s+(\d+)\)/, 'rgb($1, $2, $3)');
      }

      const backgroundRgbColor = d3.color(backgroundColor.value) as d3.RGBColor;
      const redStops: number[] = [backgroundRgbColor.r / 255];
      const greenStops: number[] = [backgroundRgbColor.g / 255];
      const blueStops: number[] = [backgroundRgbColor.b / 255];
      for (let i = 0.1; i <= 1.0; i += 0.1) {
        const rgbStopString = colorScheme.value.scheme(i);
        const color = d3.color(rgbStopString) as d3.RGBColor;
        redStops.push(color.r / 255);
        greenStops.push(color.g / 255);
        blueStops.push(color.b / 255);
      }
      rValues.value = redStops.join(' ');
      gValues.value = greenStops.join(' ');
      bValues.value = blueStops.join(' ');
      const textColor = textColorFromBackground(backgroundColor.value);
      if (freqLayer) {
        freqLayer.setTextColor(textColor);
      }
      if (speciesLayer) {
        speciesLayer.setTextColor(textColor);
      }
      if (legendLayer) {
        legendLayer.setTextColor(textColor);
      }
      if (axesLayer) {
        axesLayer.setTextColor(textColor);
      }
      if (timeLayer) {
        timeLayer.setTextColor(textColor);
      }
      if (speciesSequenceLayer) {
        speciesSequenceLayer.setTextColor(textColor);
      }
      if (measureToolLayer) {
        measureToolLayer.setTextColor(textColor);
      }
      if (boundingBoxLayer) {
        boundingBoxLayer.setTextColor(textColor);
      }
      if (contourLayer) {
        contourLayer.setColorScheme(colorScheme.value.scheme);
      }
    }

    watch([backgroundColor, colorScheme], updateColorFilter);


    watch(fixedAxes, setAxes);

    return {
      annotationState,
      localAnnotations,
      displayError,
      errorMsg,
      selectedUsers,
      rValues,
      gValues,
      bValues,
      getTransparencyTableValues,
    };
  },
});
</script>

<template>
  <v-dialog
    v-model="displayError"
    width="500"
  >
    <v-card>
      <v-card-title>Error</v-card-title>
      <v-card-text>{{ errorMsg }}</v-card-text>
      <v-card-actions>
        <v-row>
          <v-spacer />
          <v-btn
            variant="outlined"
            @click="displayError = false"
          >
            Dismiss
          </v-btn>
          <v-spacer />
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <svg
    width="0"
    height="0"
    style="position: absolute; top: -1px; left: -1px;"
  >
    <filter id="svg-filters">
      <!-- convert to grayscale -->
      <feColorMatrix
        type="saturate"
        values="0"
        result="grayscale"
      />

      <feColorMatrix
        in="grayscale"
        type="matrix"
        values="0.2126 0.7152 0.0722 0 0
                0.2126 0.7152 0.0722 0 0
                0.2126 0.7152 0.0722 0 0
                0 0 0 1 0"
        result="sourceGraphic"
      />
      
      <feColorMatrix
        in="sourceGraphic"
        type="luminanceToAlpha"
        result="luminance"
      />
      <feComponentTransfer
        in="luminance"
        result="transparency-mask"
      >
        <feFuncA
          type="table"
          :tableValues="getTransparencyTableValues()"
        />
      </feComponentTransfer>

      <feComposite
        in="grayscale"
        in2="transparency-mask"
        operator="in"
        result="grayscale-with-transparency"
      />
      <feComponentTransfer in="grayscale-with-transparency">
        <feFuncR
          type="table"
          :tableValues="rValues"
        />
        <feFuncG
          type="table"
          :tableValues="gValues"
        />
        <feFuncB
          type="table"
          :tableValues="bValues"
        />
      </feComponentTransfer>
    </filter>
  </svg>
</template>
